import React from 'react';
import dayjs from 'lib/dayjs';
import { AlertCircle, Calendar, Check, Clock, Edit3, Plus, RotateCcw, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useCancelLogUpdateRequestMutation } from 'redux/features/timeLogApi';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import useUser from '../../../hooks/useUser';

const DayLogEditModal = ({
  isOpen,
  onClose,
  onSubmit,
  date,
  logs = [],
  user: propUser,
  targetUserId,
}) => {
  const [editedLogs, setEditedLogs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCancelRequestId, setPendingCancelRequestId] = useState(null);
  const [cancelRequest, { isLoading: isCancelling }] = useCancelLogUpdateRequestMutation();

  // Use the hook for definitive role detection
  const hookUser = useUser();
  const user = hookUser || propUser;
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (isOpen) {
      // Direct data processing from the logs prop (provided by the parent's monthly query)
      const sourceLogs = logs || [];

      // Extract pending requests embedded in the logs
      // Note: Backend now includes logUpdateRequest for each log entry
      const pendingRequests = logs.map((l) => l.logUpdateRequest).filter(Boolean);

      // Separate confirmed logs from pending creates
      // Confirmed logs have an id, while pending creates (from backend) have id: null but logUpdateRequest: { type: 'CREATE' }
      const confirmedLogs = sourceLogs.filter((l) => l.id !== null);

      const initialLogs = confirmedLogs.map((log) => {
        // Check if there's a pending UPDATE or DELETE for this specific log entry
        const pendingUpdate = pendingRequests.find(
          (pr) => pr.time_log_id === log.id && pr.type === 'UPDATE',
        );
        const pendingDelete = pendingRequests.find(
          (pr) => pr.time_log_id === log.id && pr.type === 'DELETE',
        );

        const displayLog =
          pendingUpdate && !isAdmin
            ? {
                ...log,
                check_in: pendingUpdate.check_in,
                check_out: pendingUpdate.check_out,
                reason: pendingUpdate.reason,
                isPendingUpdate: true,
              }
            : { ...log, reason: '' };

        const initialReason = pendingUpdate
          ? pendingUpdate.reason
          : pendingDelete
            ? pendingDelete.reason
            : '';
        const initialCheckInTime = dayjs(displayLog.check_in).format('HH:mm');
        const initialCheckOutTime = displayLog.check_out
          ? dayjs(displayLog.check_out).format('HH:mm')
          : '';

        return {
          ...displayLog,
          isPendingDelete: !!pendingDelete && !isAdmin,
          reason: initialReason,
          check_in_time: initialCheckInTime,
          check_out_time: initialCheckOutTime,
          original_check_in: displayLog.check_in,
          original_check_out: displayLog.check_out,
          original_check_in_time: initialCheckInTime,
          original_check_out_time: initialCheckOutTime,
          original_reason: initialReason,
          requestStatus: log.logUpdateRequest?.status,
          logUpdateRequest: log.logUpdateRequest,
          isNew: false,
          isDeleted: false,
        };
      });

      // Add pending CREATE requests (identified by their type property)
      const pendingCreates = !isAdmin
        ? pendingRequests
            .filter((pr) => pr.type === 'CREATE')
            .map((pr) => {
              const checkInTime = dayjs(pr.check_in).format('HH:mm');
              const checkOutTime = pr.check_out ? dayjs(pr.check_out).format('HH:mm') : '';
              return {
                ...pr,
                id: null,
                tempId: pr.id,
                check_in_time: checkInTime,
                check_out_time: checkOutTime,
                reason: pr.reason,
                original_check_in_time: checkInTime,
                original_check_out_time: checkOutTime,
                original_reason: pr.reason,
                requestStatus: pr.status,
                logUpdateRequest: pr,
                isNew: false, // Already on server as pending, not a local new entry
                isDeleted: false,
                isPendingCreate: true,
              };
            })
        : [];

      setEditedLogs([...initialLogs, ...pendingCreates]);
      setIsSubmitting(false);
    }
  }, [isOpen, logs, isAdmin]);

  const changeStatus = React.useMemo(() => {
    const changes = editedLogs.filter((log) => {
      if (log.isNew || log.isDeleted) return true;
      return (
        log.check_in_time !== log.original_check_in_time ||
        log.check_out_time !== log.original_check_out_time ||
        log.reason !== log.original_reason
      );
    });

    return {
      hasChanges: changes.length > 0,
      isModified: (index) => {
        const log = editedLogs[index];
        if (log.isNew || log.isDeleted) return false;
        return (
          log.check_in_time !== log.original_check_in_time ||
          log.check_out_time !== log.original_check_out_time ||
          log.reason !== log.original_reason
        );
      },
    };
  }, [editedLogs]);

  // Dynamic calculations
  const stats = React.useMemo(() => {
    let totalMinutes = 0;
    let hasValidationError = false;
    const errors = {}; // { index: { check_in: '', check_out: '', reason: '', overlap: '' } }

    editedLogs.forEach((log, i) => {
      if (log.isDeleted) return;

      const rowErrors = {};
      if (!log.check_in_time) rowErrors.check_in = 'Required';
      if (!log.check_out_time) rowErrors.check_out = 'Required';

      const isLogModified = changeStatus.isModified(i) || log.isNew || log.isDeleted;
      if (isLogModified && !log.reason) rowErrors.reason = 'Reason required';

      if (log.check_in_time && log.check_out_time) {
        const [inH, inM] = log.check_in_time.split(':').map(Number);
        const [outH, outM] = log.check_out_time.split(':').map(Number);
        const duration = outH * 60 + outM - (inH * 60 + inM);
        if (duration <= 0) {
          rowErrors.check_out = 'Must be after check-in';
          hasValidationError = true;
        } else {
          totalMinutes += duration;
        }
      }

      // Overlap check
      editedLogs.forEach((other, j) => {
        if (i === j || other.isDeleted) return;
        if (
          log.check_in_time &&
          log.check_out_time &&
          other.check_in_time &&
          other.check_out_time
        ) {
          // Check if log.check_in_time falls within other session's range
          if (
            log.check_in_time >= other.check_in_time &&
            log.check_in_time < other.check_out_time
          ) {
            rowErrors.check_in_overlap = 'Overlap conflict';
            hasValidationError = true;
          }
          // Check if log.check_out_time falls within other session's range
          if (
            log.check_out_time > other.check_in_time &&
            log.check_out_time <= other.check_out_time
          ) {
            rowErrors.check_out_overlap = 'Overlap conflict';
            hasValidationError = true;
          }
        }
      });

      if (Object.keys(rowErrors).length > 0) {
        errors[i] = rowErrors;
        hasValidationError = true;
      }
    });

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return { totalHours: `${h}h ${m}m`, hasValidationError, errors };
  }, [editedLogs, changeStatus]);

  const handleAddLog = () => {
    setEditedLogs([
      ...editedLogs,
      {
        tempId: Date.now(),
        id: null,
        check_in_time: '',
        check_out_time: '',
        reason: '',
        original_check_in_time: undefined,
        original_check_out_time: undefined,
        original_reason: undefined,
        isNew: true,
        isDeleted: false,
      },
    ]);
  };

  const handleToggleDelete = (index) => {
    setEditedLogs((prev) => {
      const newLogs = [...prev];
      if (newLogs[index].isNew) {
        newLogs.splice(index, 1);
      } else {
        newLogs[index] = { ...newLogs[index], isDeleted: !newLogs[index].isDeleted };
      }
      return newLogs;
    });
  };

  const handleUpdateLog = (index, field, value) => {
    setEditedLogs((prev) => {
      const newLogs = [...prev];
      newLogs[index] = { ...newLogs[index], [field]: value };
      return newLogs;
    });
  };

  const handleUndo = (index) => {
    setEditedLogs((prev) => {
      const newLogs = [...prev];
      const log = newLogs[index];
      newLogs[index] = {
        ...log,
        check_in_time: log.original_check_in_time,
        check_out_time: log.original_check_out_time,
        reason: log.original_reason,
      };
      return newLogs;
    });
  };

  const handleCancelRequest = (index) => {
    const log = editedLogs[index];
    const requestId = log.logUpdateRequest?.id || log.tempId;
    if (!requestId) {
      toast.error('Request ID not found');
      return;
    }
    setPendingCancelRequestId(requestId);
  };

  const handleConfirmCancel = async () => {
    if (!pendingCancelRequestId) return;
    try {
      await cancelRequest(pendingCancelRequestId).unwrap();
      toast.success('Request cancelled successfully');
      setPendingCancelRequestId(null);
    } catch {
      // Error toast is handled by baseQueryV1 for API errors
    }
  };

  const handleCloseConfirmModal = () => {
    setPendingCancelRequestId(null);
  };

  const handleSubmit = async () => {
    if (stats.hasValidationError) {
      toast.error(stats.errorMsg || 'Fix all errors before submitting');
      return;
    }
    setIsSubmitting(true);
    try {
      const actions = [];
      const baseDate = dayjs(date).startOf('day');
      // Use YYYY-MM-DD to avoid timezone shift on backend when parsing
      const dateStr = baseDate.format('YYYY-MM-DD');

      editedLogs.forEach((log) => {
        const requestId = log.logUpdateRequest?.id;

        if (log.isNew) {
          actions.push({
            type: 'CREATE',
            check_in: dayjs(`${dateStr}T${log.check_in_time}`).toISOString(),
            check_out: dayjs(`${dateStr}T${log.check_out_time}`).toISOString(),
            reason: log.reason,
            request_id: requestId,
          });
        } else if (log.isDeleted) {
          actions.push({
            type: 'DELETE',
            id: log.id,
            request_id: requestId,
          });
        } else if (changeStatus.isModified(editedLogs.indexOf(log))) {
          actions.push({
            type: log.logUpdateRequest?.type || 'UPDATE',
            id: log.id,
            request_id: requestId,
            check_in: dayjs(`${dateStr}T${log.check_in_time}`).toISOString(),
            check_out: dayjs(`${dateStr}T${log.check_out_time}`).toISOString(),
            reason: log.reason,
            original_check_in: log.original_check_in,
            original_check_out: log.original_check_out,
          });
        }
      });
      await onSubmit({
        user_id: targetUserId || user.id || user.user_id,
        date: dateStr,
        actions,
      });
      // Don't close modal, let RTK Query refetch and useEffect update the state
    } catch {
      // Error toast is handled by baseQueryV1 for API errors (400/409/500)
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-6xl transform overflow-hidden rounded-md bg-white flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="bg-white px-8 py-5 border-b border-slate-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
              <div className="bg-indigo-50 p-2 text-indigo-600 rounded-lg">
                <Edit3 size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800 leading-none">Update Logs</h2>
                <div className="flex items-center gap-1.5 mt-1.5 opacity-60">
                  <Calendar size={14} />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    {dayjs(date).format('MMMM D, YYYY')}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Banner Row */}
          <div className="px-8 py-2 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4 shrink-0">
            <div className="flex justify-end w-full gap-5">
              <div className="flex items-center gap-4 py-2 px-5 bg-white rounded-xl border border-slate-200">
                <div className="flex flex-col items-end">
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter leading-none">
                    Total Hours
                  </span>
                  <span className="text-lg font-black text-indigo-600 tabular-nums leading-none mt-1">
                    {stats.totalHours}
                  </span>
                </div>
                <div className="h-8 w-px bg-slate-100" />
                <button
                  onClick={handleAddLog}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-md"
                >
                  <Plus size={14} strokeWidth={4} />
                  Add Session
                </button>
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/20 overflow-hidden">
              <div className="bg-slate-50 border-b border-slate-100 px-6 py-3">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-[0.15em]">
                    Check In
                  </div>
                  <div className="col-span-2 text-[11px] font-black text-slate-400 uppercase tracking-[0.15em]">
                    Check Out
                  </div>
                  <div className="col-span-4 text-[11px] font-black text-slate-400 uppercase tracking-[0.15em]">
                    Reason
                  </div>
                  <div className="col-span-3 text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] text-right">
                    Status & Duration
                  </div>
                  <div className="col-span-1 text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] text-right">
                    Action
                  </div>
                </div>
              </div>
              {editedLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Clock size={40} className="text-slate-200 mb-4" />
                  <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">
                    No entries found for this date
                  </p>
                  <button
                    onClick={handleAddLog}
                    className="mt-6 px-8 py-3 bg-indigo-50 text-indigo-600 rounded-xl font-bold text-[10px] uppercase tracking-widest border border-indigo-100 hover:bg-indigo-100 transition-colors"
                  >
                    Add First Session
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {editedLogs.map((log, index) => (
                    <div
                      key={log.id || log.tempId || index}
                      className={`p-2 transition-colors ${log.isDeleted ? 'bg-rose-50/30' : 'hover:bg-slate-50/30'}`}
                    >
                      <div className="grid grid-cols-12 gap-3 items-start">
                        <div className="col-span-2 relative">
                          <div className="relative">
                            <input
                              type="time"
                              value={log.check_in_time}
                              disabled={log.isDeleted}
                              onChange={(e) =>
                                handleUpdateLog(index, 'check_in_time', e.target.value)
                              }
                              className={`w-full px-2 py-1 rounded-md border focus:outline-none text-[13px] font-bold text-slate-700 bg-white disabled:opacity-40 transition-all duration-200 ${stats.errors[index]?.check_in || stats.errors[index]?.check_in_overlap ? 'border-rose-300 bg-rose-50/30' : log.check_in_time !== log.original_check_in_time ? 'border-amber-400 bg-amber-50/20 ring-1 ring-amber-400' : 'border-slate-200 focus:border-indigo-400'}`}
                            />
                          </div>
                          {(stats.errors[index]?.check_in ||
                            stats.errors[index]?.check_in_overlap) && (
                            <p className="text-[11px] text-rose-500 mt-1 font-bold leading-none p-1">
                              {stats.errors[index]?.check_in ||
                                stats.errors[index]?.check_in_overlap}
                            </p>
                          )}
                        </div>
                        <div className="col-span-2 relative">
                          <div className="relative">
                            <input
                              type="time"
                              value={log.check_out_time}
                              disabled={log.isDeleted}
                              onChange={(e) =>
                                handleUpdateLog(index, 'check_out_time', e.target.value)
                              }
                              className={`w-full px-2 py-1 rounded-md border focus:outline-none text-[13px] font-bold text-slate-700 bg-white disabled:opacity-40 transition-all duration-200 ${stats.errors[index]?.check_out || stats.errors[index]?.check_out_overlap ? 'border-rose-300 bg-rose-50/30' : log.check_out_time !== log.original_check_out_time ? 'border-amber-400 bg-amber-50/20 ring-1 ring-amber-400' : 'border-slate-200 focus:border-indigo-400'}`}
                            />
                          </div>
                          {(stats.errors[index]?.check_out ||
                            stats.errors[index]?.check_out_overlap) && (
                            <p className="text-[11px] text-rose-500 mt-1 font-bold leading-none p-1">
                              {stats.errors[index]?.check_out ||
                                stats.errors[index]?.check_out_overlap}
                            </p>
                          )}
                        </div>
                        <div className="col-span-4 flex flex-row gap-1">
                          <div className="flex flex-col w-full">
                            <input
                              type="text"
                              placeholder="Enter your reason..."
                              value={log.reason}
                              disabled={log.isDeleted}
                              onChange={(e) => handleUpdateLog(index, 'reason', e.target.value)}
                              className={`w-full px-4 py-1 rounded-md border focus:outline-none text-[13px] font-bold text-slate-700 bg-white disabled:opacity-40 placeholder:text-slate-300 transition-all duration-200 ${
                                stats.errors[index]?.reason
                                  ? 'border-rose-300 bg-rose-50/30'
                                  : log.reason !== log.original_reason
                                    ? 'border-amber-400 bg-amber-50/20 ring-1 ring-amber-400'
                                    : 'border-slate-200 focus:border-indigo-400'
                              }`}
                            />

                            {stats.errors[index]?.reason && (
                              <p className="text-[12px] text-rose-500 font-bold leading-none p-1">
                                {stats.errors[index]?.reason}
                              </p>
                            )}
                          </div>

                          {/* Always render button, control visibility */}
                          <div className="w-[28px] flex items-start justify-center">
                            <button
                              onClick={() => handleUndo(index)}
                              className={`p-1.5 text-indigo-500 rounded-lg transition-all ${
                                changeStatus.isModified(index)
                                  ? 'opacity-100'
                                  : 'opacity-0 pointer-events-none'
                              }`}
                              title="Undo changes"
                            >
                              <RotateCcw size={14} />
                            </button>
                          </div>
                        </div>
                        <div className="col-span-3 flex items-center justify-end gap-3 pt-1">
                          <div className="flex gap-1.5 flex-wrap justify-end">
                            {log.requestStatus && (
                              <span
                                className={`text-[13px] font-black px-2 py-0.5 border rounded uppercase tracking-widest whitespace-nowrap ${
                                  log.requestStatus === 'APPROVED'
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                    : log.requestStatus === 'REJECTED'
                                      ? 'bg-rose-50 text-rose-600 border-rose-100'
                                      : 'bg-amber-50 text-amber-600 border-amber-100'
                                }`}
                              >
                                {log.requestStatus}
                              </span>
                            )}
                            {log.isNew && !log.isPendingCreate && !log.requestStatus && (
                              <span className="text-[11px] font-black px-2 py-0.5 bg-emerald-50 text-emerald-600 border-emerald-100 rounded uppercase tracking-widest whitespace-nowrap">
                                New
                              </span>
                            )}
                            {log.isDeleted && (
                              <span className="text-[11px] font-black px-2 py-0.5 bg-rose-50 text-rose-600 border-rose-100 rounded uppercase tracking-widest whitespace-nowrap">
                                Deleted
                              </span>
                            )}
                          </div>
                          {!log.isDeleted && log.check_in_time && log.check_out_time && (
                            <div
                              className={`px-4 py-2 rounded-xl text-[13px] font-black border tabular-nums shadow-sm whitespace-nowrap ${calculateDuration(log.check_in_time, log.check_out_time) === 'Invalid' ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}
                            >
                              {calculateDuration(log.check_in_time, log.check_out_time)}
                            </div>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end pt-1">
                          {log.logUpdateRequest ||
                          log.isPendingCreate ||
                          log.isPendingUpdate ||
                          log.isPendingDelete ? (
                            <button
                              onClick={() => handleCancelRequest(index)}
                              disabled={isCancelling}
                              className="p-2.5 rounded-xl border bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100 transition-all"
                              title="Cancel Pending Request"
                            >
                              <X size={16} />
                            </button>
                          ) : isAdmin || log.isNew ? (
                            <button
                              onClick={() => handleToggleDelete(index)}
                              className={`p-2.5 rounded-xl border transition-all ${log.isDeleted ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100'}`}
                            >
                              {log.isDeleted ? (
                                <Plus size={16} className="rotate-45" />
                              ) : (
                                <X size={16} />
                              )}
                            </button>
                          ) : (
                            <div className="w-10 h-10 flex items-center justify-center opacity-10">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 px-8 py-4 bg-white flex items-center justify-between shrink-0">
            <div className="flex-1">
              {stats.hasValidationError ? (
                <p className="text-[10px] font-black uppercase text-rose-500 flex items-center gap-2">
                  <AlertCircle size={14} />
                  Action Required: Fix session errors
                </p>
              ) : !changeStatus.hasChanges ? (
                <p className="text-[13px] text-slate-400 font-bold uppercase tracking-widest italic">
                  No changes detected
                </p>
              ) : (
                <p className="text-[10px] text-indigo-500 font-black uppercase tracking-widest flex items-center gap-2">
                  <Check size={16} strokeWidth={3} />
                  Ready to Update
                </p>
              )}
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="px-10 py-3.5 rounded-xl border-2 border-slate-100 text-slate-500 font-black text-[11px] uppercase tracking-widest hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-100 transition-all flex items-center justify-center"
              >
                Close
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !changeStatus.hasChanges || stats.hasValidationError}
                className="px-12 py-3.5 rounded-xl bg-indigo-600 text-white font-black text-[11px] uppercase tracking-widest hover:bg-indigo-700 hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-indigo-100 disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Check size={18} strokeWidth={4} />
                )}
                <span>{isSubmitting ? 'Updating...' : 'Update'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!pendingCancelRequestId}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmCancel}
        title="Cancel Pending Request"
        message="Are you sure you want to cancel this pending request? This action cannot be undone."
        isLoading={isCancelling}
      />
    </div>
  );
};

const calculateDuration = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return '';
  const [inH, inM] = checkIn.split(':').map(Number);
  const [outH, outM] = checkOut.split(':').map(Number);
  let diff = outH * 60 + outM - (inH * 60 + inM);
  if (diff < 0) return 'Invalid';
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `${h}h ${m}m`;
};

export default DayLogEditModal;
